import { Filter, Plus, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { Card, PrimaryButton } from '../ui';
import { getAllSites } from '../SiteManagement/siteApi';
import { normalizeSites } from '../QR/qrFacilityData';
import {
  duesApi,
  getCachedDuesForSite,
  getDuesSiteId,
  normalizeDuesListResponse,
  setDuesSiteId,
} from './duesApi';
import {
  applyFlatLabelIndex,
  buildFlatLabelIndex,
  formatUnitLabel,
} from './duesDisplay';
import {
  DUE_CATEGORY_OPTIONS,
  formatCategoryLabel,
  normalizeCategory,
} from './duesUtils';

const formatCurrency = (amount) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return String(value);
  }
};

const normalizeDueForList = (due, siteName = '') => {
  const status = due?.status || (due?.payment_status === 'paid' ? 'paid' : 'open');
  const paymentStatus = due?.payment_status || (status === 'paid' ? 'paid' : 'unpaid');

  return {
    id: due?.due_id || due?.id,
    dueId: due?.due_id || due?.id,
    category: normalizeCategory(due?.category || due?.type || 'other'),
    type: formatCategoryLabel(due?.category || due?.type || 'other'),
    unit:
      formatUnitLabel({
        blockLabel: due?.block_label || due?.block_name,
        flatLabel:
          due?.flat_label ||
          due?.flat_name ||
          due?.flat_no ||
          due?.flat_number,
        flatId: due?.flat_id,
      }) || '—',
    siteName: due?.site_name || siteName || '—',
    dueDate: due?.due_date || due?.created_at || due?.createdAt || null,
    amount: Number(due?.amount || 0),
    status,
    currency: due?.currency || 'INR',
    paymentStatus,
    title: due?.title || '—',
    description: due?.description || '',
    createdAt: due?.created_at || due?.createdAt || null,
    updatedAt: due?.updated_at || due?.updatedAt || null,
    raw: due,
  };
};

const CommunityDuesTable = () => {
  const navigate = useNavigate();
  useScreenLogging('DuesTable', {
    component: 'DuesTable',
    screenType: 'dues',
  });

  const [dues, setDues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(() => getDuesSiteId());
  const [loadingSites, setLoadingSites] = useState(true);
  const [sitesError, setSitesError] = useState('');

  useEffect(() => {
    let active = true;

    const loadSites = async () => {
      setLoadingSites(true);
      setSitesError('');
      try {
        const res = await getAllSites();
        const list = normalizeSites(res).sort((a, b) => {
          const aName = String(a?.site_name || a?.name || '').toLowerCase();
          const bName = String(b?.site_name || b?.name || '').toLowerCase();
          return aName.localeCompare(bName);
        });
        if (!active) return;
        setSites(list);
        const stored = getDuesSiteId();
        const storedValid = stored && list.some((s) => s.site_id === stored);
        if (storedValid) {
          setSiteId(stored);
        } else if (list.length === 1) {
          setSiteId(list[0].site_id);
          setDuesSiteId(list[0].site_id);
        }
      } catch (e) {
        if (!active) return;
        setSites([]);
        setSitesError(e?.message || 'Failed to load sites.');
      } finally {
        if (!active) return;
        setLoadingSites(false);
      }
    };

    loadSites();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!siteId) {
      setDues([]);
      setLoading(false);
      return;
    }

    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      const siteLabel =
        sites.find((s) => String(s.site_id) === String(siteId))?.site_name || '';
      setDues(
        getCachedDuesForSite(siteId).map((d) => normalizeDueForList(d, siteLabel))
      );
      try {
        const res = await duesApi.listBySite({ site_id: siteId, limit: 50 });
        const list = normalizeDuesListResponse(res);
        if (!active) return;
        const flatIndex = await buildFlatLabelIndex(siteId);
        setDues(
          list
            .map((d) => applyFlatLabelIndex(d, flatIndex))
            .map((d) => normalizeDueForList(d, siteLabel))
        );
        setError(res?.warning || '');
      } catch (e) {
        if (!active) return;
        setDues([]);
        setError(e?.message || 'Failed to load dues.');
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [siteId, sites]);

  const filteredDues = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return dues.filter((d) => {
      if (filterType && d.category !== filterType) return false;
      if (!q) return true;
      const haystack = `${d.type} ${d.title} ${d.unit} ${d.status} ${d.paymentStatus}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [dues, searchTerm, filterType]);

  const totalAmount = useMemo(
    () =>
      filteredDues.reduce((sum, d) => sum + (Number(d.amount) || 0), 0),
    [filteredDues]
  );

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <div className="space-y-8">
          <div className="rounded-3xl">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Dues</h1>
                <p className="text-gray-600">View and manage flat dues by site</p>
              </div>

              <PrimaryButton
                onClick={() => navigate('/dues/create')}
                className="w-full sm:w-auto min-h-[48px]"
              >
                <span className="inline-flex items-center gap-2.5">
                  <Plus className="w-5 h-5" />
                  <span>Create Due</span>
                </span>
              </PrimaryButton>
            </div>

            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
              <div className="min-w-[200px] md:max-w-[280px]">
                <label
                  htmlFor="dues-site"
                  className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                >
                  Site
                </label>
                <select
                  id="dues-site"
                  value={siteId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSiteId(next);
                    setDuesSiteId(next);
                  }}
                  disabled={loadingSites}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                >
                  <option value="">
                    {loadingSites ? 'Loading sites…' : 'Select site'}
                  </option>
                  {sites.map((s) => (
                    <option key={s.site_id} value={s.site_id}>
                      {s.site_name}
                    </option>
                  ))}
                </select>
                {sitesError && (
                  <p className="mt-1 text-xs font-semibold text-red-700">{sitesError}</p>
                )}
              </div>

              <div className="min-w-[200px] flex-1">
                <label
                  htmlFor="dues-search"
                  className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                >
                  <Search className="w-4 h-4" />
                  Search
                </label>
                <input
                  id="dues-search"
                  type="text"
                  placeholder="Search by resident, unit, type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="min-w-[200px]">
                  <label
                    htmlFor="dues-type"
                    className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                  >
                    <Filter className="w-4 h-4" />
                    Type
                  </label>
                  <select
                    id="dues-type"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  >
                    <option value="">All Types</option>
                    {DUE_CATEGORY_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

              </div>
            </div>

            {error && (
              <Card className="p-4 mb-6 border border-red-200 bg-red-50 text-red-800">
                {error}
              </Card>
            )}

            <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full border-collapse text-sm md:text-base">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600 border-b border-gray-200">
                <th className="px-4 py-3 font-semibold min-w-[160px]">Type</th>
                <th className="px-4 py-3 font-semibold min-w-[180px]">Unit</th>
                <th className="px-4 py-3 font-semibold min-w-[140px]">Due Date</th>
                <th className="px-4 py-3 font-semibold min-w-[140px] text-right">
                  Amount
                </th>
                <th className="px-4 py-3 font-semibold min-w-[140px]">Status</th>
              </tr>
            </thead>
            <tbody>
              {!siteId ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    Select a site to load dues.
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    Loading dues...
                  </td>
                </tr>
              ) : filteredDues.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    No dues found.
                  </td>
                </tr>
              ) : (
                filteredDues.map((d) => (
                  <tr
                    key={d.id}
                    className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                    onClick={() =>
                      navigate(`/dues/${d.dueId || d.id}`, {
                        state: { due: d.raw || d },
                      })
                    }
                  >
                    <td className="px-4 py-4 text-gray-800">{d.type || '—'}</td>
                    <td className="px-4 py-4 text-gray-700">{d.unit || '—'}</td>
                    <td className="px-4 py-4 text-gray-700">{formatDate(d.dueDate)}</td>
                    <td className="px-4 py-4 text-right font-semibold text-gray-800">
                      {formatCurrency(d.amount)}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={
                          d.paymentStatus === 'paid' || d.status === 'paid'
                            ? 'px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800'
                            : d.status === 'cancelled'
                              ? 'px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800'
                              : 'px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800'
                        }
                      >
                        {d.paymentStatus === 'paid' || d.status === 'paid'
                          ? 'Paid'
                          : d.status === 'cancelled'
                            ? 'Cancelled'
                            : 'Open'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-4">
              <div className="text-sm text-gray-600">
                Showing{' '}
                <span className="font-semibold text-gray-900">
                  {filteredDues.length}
                </span>{' '}
                due{filteredDues.length === 1 ? '' : 's'}
              </div>
              <div className="text-sm md:text-base">
                <span className="text-gray-600 font-semibold mr-2">Total:</span>
                <span className="text-gray-900 font-bold">
                  {formatCurrency(totalAmount)}
                </span>
              </div>
            </div>

            {!filteredDues.length && (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  No dues found matching the selected criteria.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityDuesTable;

